<script setup lang="ts">
import { computed } from 'vue';

import usePoolData from '@/composables/queries/usePoolData';
import { PoolNote } from '@/types/untangled/pool';
import BigNumber from 'bignumber.js';
import { fxCommonUtil } from '@/lib/utils/fxCommon';

/**
 * TYPES
 */
type Props = {
  poolNote: PoolNote;
};

/**
 * PROPS & EMITS
 */
const props = defineProps<Props>();

/**
 * COMPOSABLES
 */

const { poolData, loading } = usePoolData({
  pool: ref(props.poolNote.pool),
  event: ref(props.poolNote),
});

/** COMPUTED */

const isHasSOT = computed(() => !!props.poolNote.targetAmount);
const isHasJOT = computed(() => !!props.poolNote.juniorTargetAmount);

const investmentCapacity = computed(() => {
  let totalCapacity = 0;

  if (isHasSOT.value) {
    totalCapacity += new BigNumber(
      poolData.value.investmentCapacityOfSOT.toString()
    )
      .dividedBy(10 ** props.poolNote?.pool?.currency?.decimals)
      .toNumber();
  }

  if (isHasJOT.value) {
    totalCapacity += new BigNumber(
      poolData.value.investmentCapacityOfJOT.toString()
    )
      .dividedBy(10 ** props.poolNote?.pool?.currency?.decimals)
      .toNumber();
  }

  return totalCapacity;
});

const currencySymbol = computed(() =>
  fxCommonUtil.getCurrencySymbol(props.poolNote.currency?.shortCode)
);
</script>

<template>
  <div class="flex justify-end py-4 px-6 -mt-1 font-numeric">
    <BalLoadingBlock v-if="loading" class="w-12 h-4" />
    <span v-else class="text-right">
      {{ `${currencySymbol}${fxCommonUtil.numberFormat(investmentCapacity)}` }}
    </span>
  </div>
</template>
